"use client";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useStripe, useElements } from "@stripe/react-stripe-js";
import type { StripePaymentElementChangeEvent } from "@stripe/stripe-js";
import { DefaultValues } from "react-hook-form";
import { z } from "zod";
import { requiredString } from "@/lib/zod";
import { useForm } from "@/hooks/useForm";
import { resolveStripeErrorMessage } from "../utils/resolveStripeErrorMessage";
import { useStripeSetupRedirect } from "./useStripeSetupRedirect";

const cardFormSchema = z.object({
  cardholderName: requiredString,
  consent: z.literal(true),
});

type CardFormValues = z.infer<typeof cardFormSchema>;

const defaultValues: DefaultValues<CardFormValues> = {
  cardholderName: "",
};

export type StripeCardSubmitConsent =
  | {
      type: "checkbox";
    }
  | {
      type: "text";
      text: string;
    };

interface Props {
  consent: StripeCardSubmitConsent;
  onSuccess: (setupIntentId: string) => Promise<void>;
  onSubmitClick?: () => void;
  onError?: () => void;
}

export function useStripeCardSubmit({
  consent,
  onSuccess,
  onSubmitClick,
  onError,
}: Props) {
  const stripe = useStripe();
  const elements = useElements();
  const [isReady, setIsReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const { form, isSubmitDisabled } = useForm<CardFormValues>(cardFormSchema, {
    defaultValues,
  });
  const formRef = useRef(form);
  useLayoutEffect(() => {
    formRef.current = form;
  });

  const { isResolvingRedirect } = useStripeSetupRedirect({
    stripe,
    onSuccess,
    onError,
    setErrorMessage: setStripeError,
  });

  useEffect(() => {
    if (consent.type === "text") {
      formRef.current.setValue("consent", true);
    }
  }, [consent.type]);

  const handleSubmit = form.handleSubmit(async ({ cardholderName }) => {
    if (!stripe || !elements) return;

    onSubmitClick?.();
    setIsSubmitting(true);
    setStripeError(null);

    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: "if_required",
      confirmParams: {
        return_url: window.location.href,
        payment_method_data: {
          billing_details: {
            name: cardholderName,
            address: {
              postal_code: "",
              city: "",
              country: "US",
              line1: "",
              line2: "",
              state: "",
            },
          },
        },
      },
    });

    if (error) {
      onError?.();
      setStripeError(resolveStripeErrorMessage(error, "useStripeCardSubmit"));
      setIsSubmitting(false);
    } else {
      await onSuccess(setupIntent.id);
      setIsSubmitting(false);
    }
  });

  const handlePaymentElementReady = () => setIsReady(true);
  const handlePaymentElementChange = (event: StripePaymentElementChangeEvent) =>
    setCompleted(event.complete);

  return {
    form,
    isReady,
    isSubmitting,
    isResolvingRedirect,
    stripeError,
    completed,
    canSubmit: Boolean(stripe) && completed && !isSubmitDisabled,
    handleSubmit,
    handlePaymentElementReady,
    handlePaymentElementChange,
  };
}
